import { Button, Drawer, Grid, Icons, Input, message, Space, Typography, useDrawerForm } from '@pankod/refine-antd'
import { useTranslate } from '@pankod/refine-core'
import { definitions } from 'interfaces'
import { useState } from 'react'
import { SEND_INVOICES_TEMPLATE_PATH, supabaseClient } from 'utility'
import { read, utils } from 'xlsx'

export const ImportFromExcelButton = () => {
  const t = useTranslate()
  const breakpoint = Grid.useBreakpoint()

  const {
    drawerProps: createDrawerProps,
    show: createShow
  } = useDrawerForm<definitions['orders']>({
    action: 'create',
    redirect: false
  })

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [messageApi, contextHolder] = message.useMessage()
  const [updateFile, setUpdateFile] = useState(null)
  const [updateFinished, setUpdateFinished] = useState<string | null>(null)

  const handleFile = (e: any) => {
    setUpdateFile(e.target.files[0])
  }

  const markAsSent = async (data: Record<number, string>) => {
    // Get orders list with the orders id's in the data using invoices table
    const { data: invoices, error: invoicesError } = await supabaseClient
      .from('invoices')
      .select('order_id, invoice_number')
      .in('order_id', Object.keys(data).map(Number))

    if (invoicesError) {
      messageApi.open({ type: 'error', content: t('notifications.errorMsg') })
      setError(true)
      setLoading(false)
      setUpdateFile(null)
      setUpdateFinished('error')
      return
    }

    // Keep only the order id's that match with the invoice in the orders table
    const orderIds = (invoices ?? [])
      // eslint-disable-next-line camelcase
      .filter(({ order_id, invoice_number }) => data[order_id] === invoice_number)
      .map((mo) => mo.order_id)

    if (orderIds.length === 0) {
      messageApi.open({
        type: 'error',
        content: t('sales.importInvoicesnoMatch')
      })
      setError(true)
      setLoading(false)
      setUpdateFile(null)
      setUpdateFinished('error')
      return
    }

    // Update invoices and make it reviewed
    const { error: invoiceError } = await supabaseClient
      .from('invoices')
      .update({ reviewed: true })
      .in('order_id', orderIds)

    if (invoiceError) {
      messageApi.open({
        type: 'error',
        content: t('notifications.errorMsg')
      })
      setError(true)
      setLoading(false)
      setUpdateFile(null)
      setUpdateFinished('error')
    }

    messageApi.open({
      type: 'success',
      content: t('notifications.successMsg')
    })
    setLoading(false)
    setUpdateFile(null)
    setUpdateFinished('ok')
  }

  function isValidData (orderId: number): boolean {
    const orderIdString = String(orderId)

    // there is not 0 or negative id
    if (orderId <= 0) return false

    // Order ID must be a number of n digits
    const orderIdRegex = /^\d+$/

    if (!orderIdRegex.test(orderIdString)) {
      return false
    }

    return true
  }

  function isValidFormat (csv: any[][]): boolean {
    return csv.every((row, index) => {
      if (row.length === 0) return true

      // If the row has more than 2 columns, we ignore it. Only can write in the first two columns
      const rest = row.slice(2)
      if (rest.some((cell) => cell !== undefined && String(cell).trim() !== '')) return false

      // Omit first row (header)
      if (index === 0) return true

      return true
    })
  }

  const updateInvoicesExcel = async (e: any) => {
    e.preventDefault()
    setError(false)
    setLoading(true)
    setUpdateFinished(null)

    const reader = new FileReader()
    reader.onload = async function (e) {
      try {
        const data = e?.target?.result
        const readedData = read(new Uint8Array(data as ArrayBuffer), { type: 'array' })
        const wsname = readedData.SheetNames[0]
        const ws = readedData.Sheets[wsname]

        const csvData: any[][] = utils.sheet_to_json(ws, { header: 1 })
        if (isValidFormat(csvData)) {
          const processedData = csvData.reduce((acc: Record<number, string>, row: any[]) => {
            if (row.length === 2) {
              const orderId = row[0]
              const invoice = row[1]
              if (isValidData(Number(orderId))) {
                acc[Number(orderId)] = invoice
              }
            }
            return acc
          }, {})
          await markAsSent(processedData)
          setLoading(false)
          setUpdateFile(null)
          setUpdateFinished('ok')
          window.location.reload()
        } else {
          messageApi.open({
            type: 'error',
            content: t('notifications.errorMsg')
          })
          setError(true)
          setLoading(false)
          setUpdateFile(null)
          setUpdateFinished('errorValidate')
        }
      } catch (err) {
        messageApi.open({
          type: 'error',
          content: t('notifications.errorMsg')
        })
        setError(true)
        setLoading(false)
        setUpdateFile(null)
        setUpdateFinished('error')
      }
    }

    if (updateFile) {
      reader.readAsArrayBuffer(updateFile)
    }
  }

  return <>
    {contextHolder}
    <Button type="primary" onClick={() => createShow()}>
      {t('sales.importInvoices.title')}
    </Button>
    <Drawer
      {...createDrawerProps}
      width={breakpoint.sm ? '800px' : '100%'}
      bodyStyle={{ padding: 0 }}
      zIndex={1001}
    >
      <Space style={{ width: '100%', display: 'grid', marginTop: '5%', marginLeft: '10%' }} direction="vertical" size={12}>
        <Typography.Text>
          {t('sales.importInvoices.title')}
        </Typography.Text>

        <a href={SEND_INVOICES_TEMPLATE_PATH} download="send-invoices-template.xlsx">
          <Button>
            <Icons.DownloadOutlined />
            {t('order.biReport.labelTemplate')}
          </Button>
        </a>

        <Typography.Text>{t('marketplace.actions.uploadExcel')}</Typography.Text>
        <Input
          accept='.xlsx'
          bordered={false}
          size='middle'
          type='file'
          key='exceluploader'
          onChange={handleFile}
        />
        <Button disabled={updateFile === null} loading={loading} type='primary'
          onClick={(e: any) => updateInvoicesExcel(e)}
        >
          {t('sales.importInvoices.updateInvoices')}
        </Button>

        {loading && <Typography style={{ marginTop: '2rem', fontWeight: 600, fontStyle: 'italic' }}>
          {t('order.biReport.labelUpdating')}
        </Typography>}
        {updateFinished && <Typography style={{ marginTop: '2rem', fontWeight: 600, fontStyle: 'italic' }}>
          {t(`sales.importInvoices.labelFinish${updateFinished}`)}
        </Typography>}

      </Space>

      {(error)
        ? <Space style={{ width: '100%', display: 'grid', marginTop: '5%', marginLeft: '10%' }} direction="vertical" size={12}>
          <Typography.Text>
            {t('sales.importInvoices.error')}
          </Typography.Text>
        </Space>
        : null}
    </Drawer>
  </>
}
